import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Heart, Shield, Lightbulb } from 'lucide-react';

export default function About() {
  const values = [
    {
      icon: <Target className="w-8 h-8" />,
      title: "Excellence",
      description: "We maintain the highest standards in everything we do, from talent selection to partnership delivery."
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Partnership",
      description: "We build genuine relationships based on mutual respect, trust, and shared success."
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Integrity",
      description: "Transparency and ethical practices guide every decision and interaction."
    },
    {
      icon: <Lightbulb className="w-8 h-8" />,
      title: "Innovation",
      description: "We stay ahead of industry trends, constantly evolving our strategies and approach."
    }
  ];

  return (
    <div className="min-h-screen pt-24" data-testid="about-page">
      {/* Hero */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl text-text-primary mb-6">
              About <span className="text-primary text-glow-red">El Diablo</span>
            </h1>
            <p className="text-text-secondary text-base md:text-lg">
              A boutique creative talent agency dedicated to empowering digital creators 
              and helping them build sustainable careers.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24 bg-surface/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-text-secondary">
                <p>
                  El Diablo was founded with a clear vision: to support 
                  talented digital creators in building sustainable, successful careers.
                </p>
                <p>
                  In today's digital landscape, authentic connection is everything. We recognized 
                  that creators needed a partner who truly understood the nuances 
                  of building a presence and growing an audience in the digital space.
                </p>
                <p>
                  Today, we represent a curated roster of exceptional talent, providing 
                  management and social media expertise that delivers real results.
                </p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="glass p-8"
            >
              <h3 className="font-heading text-2xl text-text-primary mb-4">Our Mission</h3>
              <p className="text-text-secondary mb-6">
                To empower digital creators to build sustainable careers through 
                expert management and strategic social media guidance.
              </p>
              <h3 className="font-heading text-2xl text-text-primary mb-4">Our Vision</h3>
              <p className="text-text-secondary">
                To be the most trusted creative talent agency known for integrity, 
                innovation, and measurable results.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-4">
              Our <span className="text-primary text-glow-red">Values</span>
            </h2>
            <p className="text-text-secondary max-w-2xl mx-auto">
              The principles that guide everything we do.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {values.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="glass p-6 text-center hover:border-primary/50 transition-colors"
              >
                <div className="text-primary mb-4 flex justify-center">{value.icon}</div>
                <h3 className="font-heading text-lg text-text-primary mb-2">
                  {value.title}
                </h3>
                <p className="text-text-secondary text-sm">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-surface/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="font-heading text-3xl md:text-4xl text-text-primary mb-6">
              Let's Work Together
            </h2>
            <p className="text-text-secondary mb-8">
              Ready to take your career to the next level? We'd love to hear from you.
            </p>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary inline-flex items-center gap-2"
              >
                Get In Touch
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
